import React from 'react';
import styled from 'styled-components';

import { Translation, useTranslation } from '~/components/Translation';
import {
	CTARow,
	ImageBlock,
	Image640x560,
	MultipleSrcPicture,
	Signup,
	Testimonial,
	Header,
} from '~/components/Marketing';
import LinkBody from '~/components/LinkBodyV2';
import Avatar from '~/components/Avatar';
import { media, visibility } from '~/styles/mixins';
import analytics from '~/services/analytics';
import usePageTrack from '~/services/analytics/usePageTrack';
import featureFlags from '~/constants/featureFlags';

import HeroImageLargePng from './hero_image_large.png';
import HeroImageLargeWebp from './hero_image_large.webp';

import HeroImageSmallPng from './hero_image_small.png';
import HeroImageSmallWebp from './hero_image_small.webp';

import BlockImagePersonalizePng from './block_image_personalize_branding.png';
import BlockImagePersonalizeWebp from './block_image_personalize_branding.webp';

import BlockImageEasyPng from './block_image_easy.png';
import BlockImageEasyWebp from './block_image_easy.webp';

import BlockImageEverythingYouNeedPng from './block_image_everything_you_need.png';
import BlockImageEverythingYouNeedWebp from './block_image_everything_you_need.webp';

import MichelAvatarPng from './quote_avatar_michel.png';

import testimonialImgPng from './testimonial_matt.png';
import testimonialImgWebp from './testimonial_matt.webp';
import MobileLandingSignup from '../../../Signup/scenes/MobileLandingSignup';

const HeroImageLarge = styled.div`
	width: 100%;
	max-width: 1200px;
	margin: 64px auto 24px auto;
	${visibility.showMedium}
`;

const HeroImageLargeImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const HeroImageSmall = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 16px auto;
	padding: 0 24px;
	${media.medium`
    padding: 0 80px;
  `}
	${visibility.hideMedium}
`;

const HeroImageSmallImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const Image752x560 = styled(MultipleSrcPicture)`
	width: 100%;
	display: block;
	max-width: 752px;
	height: auto;
`;

const TestimonialBlock = styled.div`
	width: 100%;
	padding: 64px 0;
	${media.large`
    padding: 112px 0;
  `}
`;

const BrandedStreams = () => {
	usePageTrack({
		name: analytics.events.BRANDED_STREAMS,
		category: analytics.events.category.MARKETING,
	});

	return (
		<main role="main">
			<Header
				title={<Translation tid="landingBrandedStreams.title" />}
				subtitles={[
					<Translation key="1" tid="landingBrandedStreams.subtitle" />,
				]}
				cta={<Signup />}
				redesign={featureFlags.orion.landingPageRedesignV2}
			/>
			<HeroImageLarge>
				<HeroImageLargeImage
					fallbackSrc={HeroImageLargePng}
					webpSrc={HeroImageLargeWebp}
					alt=""
				/>
			</HeroImageLarge>
			<HeroImageSmall>
				<HeroImageSmallImage
					fallbackSrc={HeroImageSmallPng}
					webpSrc={HeroImageSmallWebp}
					alt=""
				/>
			</HeroImageSmall>
			<MobileLandingSignup />
			<ImageBlock
				body={useTranslation(
					'landingBrandedStreams.block.personalizeBranding.body',
				)}
				header={useTranslation(
					'landingBrandedStreams.block.personalizeBranding.header',
				)}
				image={
					<Image640x560
						fallbackSrc={BlockImagePersonalizePng}
						webpSrc={BlockImagePersonalizeWebp}
						alt=""
					/>
				}
				name={useTranslation('landingBrandedStreams.block.michel.quote.name')}
				role={useTranslation('landingBrandedStreams.block.michel.quote.role')}
				quote={useTranslation('landingBrandedStreams.block.michel.quote')}
				avatar={<Avatar src={MichelAvatarPng} alt="" />}
			/>
			<ImageBlock
				body={useTranslation('landingBrandedStreams.block.easyToLookGood.body')}
				color="slate"
				header={useTranslation(
					'landingBrandedStreams.block.easyToLookGood.header',
				)}
				image={
					<Image640x560
						fallbackSrc={BlockImageEasyPng}
						webpSrc={BlockImageEasyWebp}
						alt=""
					/>
				}
				reverse={true}
			/>
			<ImageBlock
				header={useTranslation(
					'landingBrandedStreams.block.everythingYouNeed.header',
				)}
				image={
					<Image752x560
						fallbackSrc={BlockImageEverythingYouNeedPng}
						webpSrc={BlockImageEverythingYouNeedWebp}
						alt=""
					/>
				}
				listItems={[
					<Translation
						key="landingBrandedStreams.block.everythingYouNeed.listItem.one"
						tid="landingBrandedStreams.block.everythingYouNeed.listItem.one"
					/>,
					<Translation
						key="landingBrandedStreams.block.everythingYouNeed.listItem.two"
						tid="landingBrandedStreams.block.everythingYouNeed.listItem.two"
					/>,
					<Translation
						key="landingBrandedStreams.block.everythingYouNeed.listItem.three"
						tid="landingBrandedStreams.block.everythingYouNeed.listItem.three"
					/>,
					<Translation
						key="landingBrandedStreams.block.everythingYouNeed.listItem.four"
						tid="landingBrandedStreams.block.everythingYouNeed.listItem.four"
					/>,
					<Translation
						key="landingBrandedStreams.block.everythingYouNeed.listItem.five"
						tid="landingBrandedStreams.block.everythingYouNeed.listItem.five"
					/>,
				]}
			/>
			<TestimonialBlock>
				<Testimonial
					altText="Matt Schick"
					imgFallbackSrc={testimonialImgPng}
					imgWebpSrc={testimonialImgWebp}
					name={
						<Translation tid="landingBrandedStreams.testimonial.matt.name" />
					}
					quote={
						<Translation tid="landingBrandedStreams.testimonial.matt.quote" />
					}
				/>
			</TestimonialBlock>
			<CTARow
				body={
					<Translation
						tid="landingBrandedStreams.cta.body"
						components={{
							link: <LinkBody to="/contact-us" color="onPrimary" />,
						}}
					/>
				}
				cta={<Signup />}
				subtitle={<Translation tid="landingBrandedStreams.cta.subtitle" />}
				title={<Translation tid="landingBrandedStreams.cta.title" />}
			/>
		</main>
	);
};

export default BrandedStreams;
